import React, { useEffect, useMemo, useState } from 'react';
import { Button, FormGroup, Input, Label, Spinner } from 'reactstrap';
import axios from '../../utils/axiosConfig';
import Swal from 'sweetalert2';
import SwitchButton3 from '../../componentes/SwitchButton3';
import '../../Pages/Css/modificaciones.css';

const INITIAL_FORM = {
    proyecto: '',
    device: '',
    ubicacion: '',
    observacion: '',
    causal: '',
};

const ModalModificaciones = ({ isOpen }) => {
    const [projects, setProjects] = useState([]);
    const [form, setForm] = useState(INITIAL_FORM);
    const [loadingProjects, setLoadingProjects] = useState(false);
    const [devices, setDevices] = useState([]);
    const [deviceSearch, setDeviceSearch] = useState('');
    const [loadingDevices, setLoadingDevices] = useState(false);
    const [executing, setExecuting] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setForm(INITIAL_FORM);
            return;
        }

        const loadProjects = async () => {
            setLoadingProjects(true);
            try {
                const response = await axios.get(`${process.env.REACT_APP_SERVER}/operaciones/getproyectos`);
                if (response.data?.success === false) {
                    throw new Error(response.data?.message || response.data?.mensaje || 'No se pudieron cargar los proyectos.');
                }
                const projectList = Array.isArray(response.data?.data) ? response.data.data : [];
                setProjects(projectList);
                setForm((current) => ({
                    ...current,
                    proyecto: current.proyecto || String(projectList[0]?.IDProyecto || ''),
                }));
            } catch (error) {
                Swal.fire('Error', error.response?.data?.message || error.message || 'Error cargando los proyectos.', 'error');
            } finally {
                setLoadingProjects(false);
            }
        };

        loadProjects();
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !form.proyecto) {
            setDevices([]);
            return;
        }

        const loadDevices = async () => {
            setLoadingDevices(true);
            setDevices([]);
            setDeviceSearch('');
            setForm((current) => ({ ...current, device: '' }));
            try {
                const response = await axios.post(
                    `${process.env.REACT_APP_SERVER}/operaciones/getdispositivosruta`,
                    { proyectoorigen: Number(form.proyecto) }
                );
                if (response.data?.success === false) {
                    throw new Error(response.data?.message || response.data?.mensaje || 'No se pudieron cargar los dispositivos.');
                }
                const data = response.data?.data;
                const deviceList = data?.recordsets?.[0] || data?.recordset || (Array.isArray(data) ? data : []);
                setDevices(deviceList);
            } catch (error) {
                Swal.fire('Error', error.response?.data?.message || error.message || 'Error cargando los dispositivos.', 'error');
            } finally {
                setLoadingDevices(false);
            }
        };

        loadDevices();
    }, [form.proyecto, isOpen]);

    const filteredDevices = useMemo(() => {
        const search = deviceSearch.trim().toLowerCase();
        if (!search) return devices;
        return devices.filter((device) => String(device?.DeviceID || '').toLowerCase().includes(search));
    }, [devices, deviceSearch]);

    const updateField = (event) => {
        const { name, value } = event.target;
        setForm((current) => ({ ...current, [name]: value }));
    };

    const clearForm = () => {
        setForm({ ...INITIAL_FORM, proyecto: String(projects[0]?.IDProyecto || '') });
        setDeviceSearch('');
    };

    const execute = async (event) => {
        event.preventDefault();
        if (Object.values(form).some((value) => !String(value).trim())) {
            Swal.fire('Información', 'Complete todos los campos para ejecutar la modificación.', 'info');
            return;
        }

        const payload = {
            device: form.device,
            ubicacion: form.ubicacion.trim(),
            nota: form.observacion.trim(),
            proyecto: Number(form.proyecto),
            causal: form.causal.trim(),
        };

        console.log('Payload para cerrar dispositivo:', payload);
        setExecuting(true);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_SERVER}/contratos/cerrardispositivo`,
                payload
            );
            console.log('Respuesta al cerrar dispositivo:', response.data);
            if (response.data?.success === false) {
                throw new Error(response.data?.message || response.data?.mensaje || 'No fue posible ejecutar la modificación.');
            }
            await Swal.fire('¡Éxito!', 'La modificación se ejecutó correctamente.', 'success');
            clearForm();
        } catch (error) {
            Swal.fire(
                'Error',
                error.response?.data?.message || error.response?.data?.mensaje || error.message || 'Error ejecutando la modificación.',
                'error'
            );
        } finally {
            setExecuting(false);
        }
    };

    return (
        <form className="modifications-form" onSubmit={execute}>
            <FormGroup>
                <Label for="modifications-project">Proyecto</Label>
                <Input
                    id="modifications-project"
                    name="proyecto"
                    type="select"
                    value={form.proyecto}
                    onChange={updateField}
                    disabled={loadingProjects}
                >
                    <option value="">{loadingProjects ? 'Cargando proyectos...' : 'Seleccione un proyecto'}</option>
                    {projects.map((project) => (
                        <option key={project.IDProyecto} value={project.IDProyecto}>
                            {project.Descripcion}
                        </option>
                    ))}
                </Input>
            </FormGroup>

            <FormGroup>
                <Label for="modifications-device-search">Device</Label>
                <div className="modifications-device-list">
                    <div className="modifications-device-search">
                        <Input
                            id="modifications-device-search"
                            value={deviceSearch}
                            onChange={(event) => setDeviceSearch(event.target.value)}
                            placeholder="Buscar dispositivo..."
                            disabled={!form.proyecto || loadingDevices}
                        />
                    </div>
                    {loadingDevices ? (
                        <div className="modifications-device-status"><Spinner size="sm" /> Cargando dispositivos...</div>
                    ) : filteredDevices.length > 0 ? (
                        filteredDevices.map((device) => (
                            <div className="modifications-device-option" key={device.DeviceID}>
                                <SwitchButton3
                                    checked={form.device === String(device.DeviceID)}
                                    onChange={(active) => setForm((current) => ({
                                        ...current,
                                        device: active ? String(device.DeviceID) : '',
                                    }))}
                                    type={device.DeviceID}
                                />
                            </div>
                        ))
                    ) : (
                        <div className="modifications-device-status">
                            {form.proyecto ? 'No hay dispositivos para mostrar.' : 'Seleccione un proyecto.'}
                        </div>
                    )}
                </div>
            </FormGroup>

            <FormGroup>
                <Label for="modifications-location">Ubicación</Label>
                <Input id="modifications-location" name="ubicacion" value={form.ubicacion} onChange={updateField} autoComplete="off" />
            </FormGroup>

            <FormGroup>
                <Label for="modifications-observation">Observación</Label>
                <Input id="modifications-observation" name="observacion" type="textarea" rows="3" value={form.observacion} onChange={updateField} />
            </FormGroup>

            <FormGroup>
                <Label for="modifications-cause">Causal</Label>
                <Input id="modifications-cause" name="causal" type="textarea" rows="3" value={form.causal} onChange={updateField} />
            </FormGroup>

            <div className="modifications-actions">
                <Button type="submit" className="modifications-primary-button" disabled={loadingProjects || loadingDevices || executing}>
                    {executing ? <><Spinner size="sm" /> Ejecutando...</> : 'Ejecutar'}
                </Button>
                <Button type="button" color="light" onClick={clearForm} disabled={executing}>Limpiar</Button>
            </div>
        </form>
    );
};

export default ModalModificaciones;
